import React, { useState, useEffect } from 'react';
import '../fonts.css';
import Changelog from './Changelog';
import CryptoApi from './CryptoApi';
import BitcoinCalc from './BitcoinCalc';
import gsap from 'gsap';

function TextFrame({ position, vertical = 'top', text, href }) {
    const [displayText, setDisplayText] = useState('');

    useEffect(() => {
        let currentIndex = 0;
        const intervalId = setInterval(() => {
            if (currentIndex <= text.length) {
                setDisplayText(text.slice(0, currentIndex));
                currentIndex++;
            } else {
                clearInterval(intervalId);
            }
        }, 50);

        return () => clearInterval(intervalId);
    }, [text]);

    const baseStyle = {
        position: 'fixed',
        [vertical]: '20px',
        [position]: '5px',
        width: '200px',
        height: 'auto',
        padding: '8px',
        color: 'white',
        fontFamily: 'HelveticaNeue, Arial, sans-serif',
        fontSize: '10px',
        letterSpacing: '0.05em',
        lineHeight: '1.4',
        zIndex: 1000,
        pointerEvents: href ? 'auto' : 'none',
        textAlign: position === 'right' ? 'right' : 'left',
        whiteSpace: 'pre-line'
    };

    const linkStyle = {
        ...baseStyle,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.3s ease',
        ':hover': {
            color: '#ff6b6b'
        }
    };

    return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {displayText}
        </a>
    ) : (
        <div style={baseStyle}>
            {displayText}
        </div>
    );
}


function Controls({ 
    groundType, 
    setGroundType, 
    spotlightColors, 
    setSpotlightColors, 
    spotlightIntensity, 
    setSpotlightIntensity, 
    isEngineOn, 
    setIsEngineOn, 
    soundGraphics,
    signatureColor,
    setSignatureColor,
    scrollToAnchor
}) {
    const colorSchemes = {
        default: {
            primary: [1, 0.25, 0.7],
            secondary: [0.14, 0.5, 1]
        },
        warm: {
            primary: [1, 0.5, 0.2],
            secondary: [1, 0.8, 0.4]
        },
        cool: {
            primary: [0.2, 0.5, 1],
            secondary: [0.4, 0.8, 1]
        }
    };

    const handleGroundChange = (type) => {
        console.log('Ground Type: Changing to', type);
        setGroundType(type);
    };

    const handleLightingChange = (scheme) => {
        console.log('Lighting: Changing to', scheme);
        setSpotlightColors(colorSchemes[scheme]);
    };

    const buttonStyle = {
        border: '1px solid rgba(255, 255, 255, 0.3)',
        padding: '5px 15px',
        borderRadius: '3px',
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s',
        fontFamily: 'HelveticaNeue, Arial, sans-serif',
        fontSize: '12px',
        letterSpacing: '0.05em',
        background: 'transparent',
    };

    const activeButtonStyle = {
        ...buttonStyle,
        border: '1px solid rgba(255, 255, 255, 0.8)',
        background: 'transparent',
        boxShadow: '0 0 10px rgba(255, 255, 255, 0.1)',
    };

    const sliderStyle = {
        width: '100%',
        maxWidth: '200px',
        margin: '0 10px',
        background: 'transparent',
        cursor: 'pointer',
        WebkitAppearance: 'none',
        appearance: 'none'
    };

    const isCurrentScheme = (scheme) => {
        return JSON.stringify(spotlightColors) === JSON.stringify(colorSchemes[scheme]);
    };

    const handleEngineToggle = () => {
        setIsEngineOn(!isEngineOn);
        if (!isEngineOn) {
            requestAnimationFrame(() => {
                window.scrollTo({
                    top: window.innerHeight,
                    behavior: 'instant'
                });
            });
        }
    };

    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            alignItems: 'center',
            background: 'rgba(0, 0, 0, 0.5)',
            padding: '15px 25px',
            borderRadius: '5px',
            color: 'white',
            fontFamily: 'HelveticaNeue, Arial, sans-serif',
            fontSize: '12px',
            letterSpacing: '0.05em',
        }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span>Ground Type:</span>
                <button
                    onClick={() => handleGroundChange('asphalt')}
                    style={groundType === 'asphalt' ? activeButtonStyle : buttonStyle}
                >
                    Asphalt
                </button>
                <button
                    onClick={() => handleGroundChange('ground')}
                    style={groundType === 'ground' ? activeButtonStyle : buttonStyle}
                >
                    Ground
                </button>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span>Lighting:</span>
                <button
                    onClick={() => handleLightingChange('default')}
                    style={isCurrentScheme('default') ? activeButtonStyle : buttonStyle}
                >
                    Default
                </button>
                <button
                    onClick={() => handleLightingChange('warm')}
                    style={isCurrentScheme('warm') ? activeButtonStyle : buttonStyle}
                >
                    Warm
                </button>
                <button
                    onClick={() => handleLightingChange('cool')}
                    style={isCurrentScheme('cool') ? activeButtonStyle : buttonStyle}
                >
                    Cool
                </button>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%', justifyContent: 'center' }}>
                <span>Intensity:</span>
                <input
                    type="range"
                    min="100"
                    max="750"
                    value={spotlightIntensity}
                    onChange={(e) => setSpotlightIntensity(Number(e.target.value))}
                    style={sliderStyle}
                />
                <span>{spotlightIntensity}</span>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span>Engine:</span>
                <button
                    onClick={handleEngineToggle}
                    style={isEngineOn ? activeButtonStyle : buttonStyle}
                >
                    {isEngineOn ? 'Running' : 'Off'}
                </button>
            </div>
        </div>
    );
}

function DownloadButton({ setIsMoving, isCarVisible, setIsCarVisible }) {
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = () => {
        setIsCarVisible(!isCarVisible);
        if (!isCarVisible) {
            setIsMoving(false);
        }
    };

    const buttonStyle = {
        background: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        padding: '15px 30px',
        borderRadius: '5px',
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontFamily: 'HelveticaNeue, Arial, sans-serif',
        fontSize: '12px',
        letterSpacing: '0.05em',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        backdropFilter: 'blur(5px)',
    };

    if (isCarVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            pointerEvents: 'auto'
        }}>
            <button
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    ...buttonStyle,
                    boxShadow: isHovered ? '0 0 15px rgba(255, 255, 255, 0.2)' : 'none',
                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                }}
            >
                Show Car Model
            </button>
        </div>
    );
}

// Add the keyframes for the loading spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1000;
}

.fade-element {
    transition: opacity 1s ease-in-out;
}

.fade-element.hidden {
    opacity: 0;
    pointer-events: none;
}

.controls {
    position: fixed;
    top: 20px;
    left: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    pointer-events: auto;
}

.navigation-buttons {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: row;
    gap: 10px;
    background: rgba(0, 0, 0, 0.5);
    padding: 15px;
    border-radius: 10px;
    z-index: 1001;
    pointer-events: auto;
}

.navigation-buttons button {
    background: transparent;
    border: 1px solid currentColor;
    padding: 8px 16px;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: white;
    font-family: HelveticaNeue, Arial, sans-serif;
    font-size: 12px;
    letter-spacing: 0.05em;
}

.navigation-buttons button:hover {
    background: rgba(255, 255, 255, 0.1);
}

.crypto-price {
    position: fixed;
    left: 5px;
    top: 200px;
    width: 200px;
    height: auto;
    padding: 8px;
    color: white;
    font-family: HelveticaNeue, Arial, sans-serif;
    font-size: 15px;
    letter-spacing: 0.05em;
    line-height: 1.4;
    z-index: 1000;
    pointer-events: auto;
    text-align: left;
    white-space: pre-line;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
}

.crypto-price .price {
    font-weight: normal;
}

.crypto-price .currency {
    color: white;
    margin-right: 2px;
}

.crypto-price .amount {
    color: white;
}

.crypto-price .loading {
    color: white;
}

.crypto-price .error {
    color: white;
}

.bitcoin-calc {
    position: fixed;
    left: 5px;
    top: 250px;
    width: 200px;
    height: auto;
    padding: 8px;
    color: white;
    font-family: HelveticaNeue, Arial, sans-serif;
    font-size: 15px;
    letter-spacing: 0.05em;
    line-height: 1.4;
    z-index: 1000;
    pointer-events: auto;
    text-align: left;
    white-space: pre-line;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
}

.bitcoin-calc .price {
    font-weight: normal;
}

.bitcoin-calc .currency {
    color: white;
    margin-right: 2px;
}

.bitcoin-calc .amount {
    color: white;
}

.bitcoin-calc .loading {
    color: white;
}

.bitcoin-calc .error {
    color: white;
}
`;
document.head.appendChild(styleSheet);

export default function Overlay({ 
    groundType, 
    setGroundType,
    spotlightColors,
    setSpotlightColors,
    isMoving,
    setIsMoving,
    isCarVisible,
    setIsCarVisible,
    spotlightIntensity,
    setSpotlightIntensity,
    isEngineOn,
    setIsEngineOn,
    soundGraphics,
    signatureColor,
    setSignatureColor
}) {
    const topRightText = `Engine: Twin-Turbo 13B-REW
Power: 276 HP
Torque: 231 lb-ft
Weight: 2,800 lbs
0-60: 5.2s`;

    const topLeftText = `Model: RX-7 FD
Year: 1992-2002
Type: Sports car
Layout: FR
Transmission: 5-speed manual`;

    const bottomLeftText = `Dimensions:
Length: 169.3 in
Width: 68.9 in
Height: 48.4 in
Wheelbase: 95.5 in`;

    const bottomRightText = `Created by Ivan Chernoborodov
Powered by Three.js & React Three Fiber`;

    const scrollToAnchor = (anchorId) => {
        const anchor = document.getElementById(anchorId);
        if (!anchor) {
            console.error(`Anchor ${anchorId} not found`);
            return;
        }

        gsap.to(window, {
            scrollTo: {
                y: anchor.offsetTop,
                autoKill: false
            },
            duration: 0,
            ease: "none",
            onComplete: () => {
                console.log(`Scroll to ${anchorId} completed`);
            }
        });
    };

    const [isAtEndPosition, setIsAtEndPosition] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            const currentScroll = window.scrollY;
            const isEnd = currentScroll >= maxScroll * 0.9;
            setIsAtEndPosition(isEnd);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="overlay">
            <div className="navigation-buttons">
                <button 
                    onClick={() => scrollToAnchor('start-anchor')}
                    style={{ color: signatureColor }}
                >
                    Main
                </button>
                
                <button 
                    onClick={() => scrollToAnchor('circle-anchor')}
                    style={{ color: signatureColor }}
                >
                    Exhaust
                </button>
                
                <button 
                    onClick={() => scrollToAnchor('end-anchor')}
                    style={{ color: signatureColor }}
                >
                    Info
                </button>
            </div>

            <div className={`fade-element ${isAtEndPosition ? 'hidden' : ''}`}>
                <TextFrame position="right" text={topRightText} />
                <TextFrame position="left" text={topLeftText} />
                <TextFrame position="left" vertical="bottom" text={bottomLeftText} />
                <TextFrame 
                    position="right" 
                    vertical="bottom" 
                    text={bottomRightText}
                    href="https://www.linkedin.com/in/ivan-chernoborodov-38a08824a/"
                />
                {isCarVisible && (
                    <>
                        <CryptoApi />
                        <BitcoinCalc />
                    </>
                )}
                <Changelog />
                <Controls 
                    groundType={groundType} 
                    setGroundType={setGroundType}
                    spotlightColors={spotlightColors}
                    setSpotlightColors={setSpotlightColors}
                    spotlightIntensity={spotlightIntensity}
                    setSpotlightIntensity={setSpotlightIntensity}
                    isEngineOn={isEngineOn}
                    setIsEngineOn={setIsEngineOn}
                    soundGraphics={soundGraphics}
                    signatureColor={signatureColor}
                    setSignatureColor={setSignatureColor}
                    scrollToAnchor={scrollToAnchor}
                />
                <DownloadButton 
                    isMoving={isMoving} 
                    setIsMoving={setIsMoving}
                    isCarVisible={isCarVisible}
                    setIsCarVisible={setIsCarVisible}
                />
            </div>
        </div>
    );
} 